import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { BodyText, HeaderText } from "@/components/ui/Text";
import { formatCurrency } from "@/lib/currency";
import { cn } from "@/lib/utils";
import { ServiceRequest, getDisplay } from "@curai/js-fhir-tools";
import { formatDate } from "date-fns";
import { CircleCheck, TestTubeDiagonal } from "lucide-react";
import type { ChatMessage } from "../../types/chatMessageTypes";

export const ConfirmLabsMessage = ({ message }: { message: ChatMessage }) => {
  const isAsync = message.modelType === "AsyncMessage";
  const serviceRequests = message.labRequests
    ? message.labRequests.map((request) => new ServiceRequest(request))
    : [];
  const totalPrice = serviceRequests.reduce((acc, request) => {
    return acc + (request.requestPrice || 0);
  }, 0);
  const cancelled = serviceRequests.some(
    (request) => request.status === ServiceRequest.Status.REVOKED,
  );

  const final = message.final;
  const cardTitle =
    final && cancelled
      ? "Your lab order has been cancelled"
      : final
        ? "Your lab order has been confirmed"
        : "Your licensed clinician has sent you a lab order to confirm";

  return (
    <div className="flex flex-col gap-1">
      <Card
        className={cn(
          "border w-full border-info",
          isAsync && "border-secondary",
        )}
        data-testid="care-plan-card"
      >
        <CardHeader>
          <div className="flex flex-col gap-1">
            <div className="flex items-start gap-1">
              <TestTubeDiagonal className="w-4 h-4" />
              <div className="flex flex-col">
                <CardTitle>{cardTitle}</CardTitle>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Separator className="my-1" />
          {serviceRequests.map((request) => (
            <div key={request.id} className="flex flex-col gap-1">
              <div className="flex flex-row gap-1">
                {final && <CircleCheck className="w-4 h-4" color="green" />}
                <BodyText size="sm" textColor="primary">
                  {request.code ? getDisplay(request.code) : "Unknown lab"}
                </BodyText>
              </div>
              {!final && (
                <BodyText size="sm" textColor="primary">
                  {formatCurrency(request.requestPrice || 0)}
                </BodyText>
              )}
            </div>
          ))}
          {!final && serviceRequests.length > 1 && (
            <>
              <Separator className="my-1" />
              <div className="flex flex-col gap-1">
                <HeaderText size="sm" textColor="primary">
                  Total cost of labs
                </HeaderText>
                <HeaderText size="sm" textColor="primary">
                  {formatCurrency(totalPrice)}
                </HeaderText>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <BodyText size="xs" textColor={"tertiary"}>
        {formatDate(new Date(message.created), "MM/dd/yyyy h:mm a")}
      </BodyText>
    </div>
  );
};
